import type { ErrorTexts } from "@nexxt/app-kit";
import type { AssistantErrorCode } from "@nexxt/contracts/assistant";
import type { BookingErrorCode } from "@nexxt/contracts/booking";
import type { CatalogErrorCode } from "@nexxt/contracts/catalog";
import type { ClaimsErrorCode } from "@nexxt/contracts/claims";
import type { DocumentsErrorCode } from "@nexxt/contracts/documents";
import type { FilesErrorCode } from "@nexxt/contracts/files";
import type {
  ConnectionErrorCode,
  DelegatedAccessErrorCode,
  IdentityErrorCode,
  PreparedActionErrorCode,
} from "@nexxt/contracts/identity";
import type { ErpFileErrorCode, IntegrationReadErrorCode } from "@nexxt/contracts/integration";
import type { LogisticsErrorCode } from "@nexxt/contracts/logistics";
import type { NotificationsErrorCode } from "@nexxt/contracts/notifications";
import type { PartyErrorCode } from "@nexxt/contracts/party";
import type { ProcurementErrorCode } from "@nexxt/contracts/procurement";
import type { ReferenceErrorCode } from "@nexxt/contracts/reference";
import type { SalesErrorCode } from "@nexxt/contracts/sales";
import { m } from "../paraglide/messages.js";

type ErrorCode =
  | CatalogErrorCode
  | FilesErrorCode
  | ReferenceErrorCode
  | BookingErrorCode
  | ProcurementErrorCode
  | PartyErrorCode
  | SalesErrorCode
  | DocumentsErrorCode
  | IdentityAdminErrorCode
  | DelegatedAccessErrorCode
  | ConnectionErrorCode
  | PreparedActionErrorCode
  | LogisticsErrorCode
  | NotificationsErrorCode
  | IntegrationReadErrorCode
  | ErpFileErrorCode
  | ClaimsErrorCode
  | AssistantErrorCode;

/** The codes of identity that reach Backstage: its routes on an external user (ADR-148); the sign-in ones are Atelier's. */
type IdentityAdminErrorCode = Extract<
  IdentityErrorCode,
  | "not_external_user"
  | "invitation_not_pending"
  | "invitation_not_sent"
  // the reset of the second factors with four eyes (ADR-201)
  | "mfa_not_enrolled"
  | "callback_contact_invalid"
  | "mfa_reset_pending"
  | "mfa_reset_not_pending"
  | "mfa_reset_unavailable"
>;

// The words of the domains' codes the Backstage gateway can answer with (ADR-046), registered at start (main.tsx);
// the common ones are app-kit's. A code without a text does not compile.
export const errorTexts: ErrorTexts<ErrorCode> = {
  linesheet_name_taken: m.error_linesheet_name_taken,
  linesheet_header_invalid: m.error_linesheet_header_invalid,
  linesheet_not_editable: m.error_linesheet_not_editable,
  linesheet_not_ready_for_submission: m.error_linesheet_not_ready_for_submission,
  linesheet_not_ready_for_approval: m.error_linesheet_not_ready_for_approval,
  linesheet_already_synced: m.error_linesheet_already_synced,
  linesheet_not_synced: m.error_linesheet_not_synced,
  attributes_not_ready_for_sync: m.error_attributes_not_ready_for_sync,
  linesheet_not_ready_for_sync: m.error_linesheet_not_ready_for_sync,
  sync_in_progress: m.error_sync_in_progress,
  linesheet_lines_invalid: m.error_linesheet_lines_invalid,
  product_codes_invalid: m.error_product_codes_invalid,
  product_images_invalid: m.error_product_images_invalid,
  line_deletion_needs_request: m.error_line_deletion_needs_request,
  price_setup_incomplete: m.error_price_setup_incomplete,
  price_setup_not_confirmed: m.error_price_setup_not_confirmed,
  intercompany_rule_invalid: m.error_intercompany_rule_invalid,
  prices_incomplete: m.error_prices_incomplete,
  attribute_task_exists: m.error_attribute_task_exists,
  attributes_not_editable: m.error_attributes_not_editable,
  attributes_invalid: m.error_attributes_invalid,
  attributes_incomplete: m.error_attributes_incomplete,
  import_in_progress: m.error_import_in_progress,
  import_file_not_usable: m.error_import_file_not_usable,
  import_replace_not_allowed: m.error_import_replace_not_allowed,
  invitation_not_needed: m.error_invitation_not_needed,
  sales_campaign_closed: m.error_sales_campaign_closed,
  file_type_not_allowed: m.error_file_type_not_allowed,
  file_too_large: m.error_file_too_large,
  file_content_mismatch: m.error_file_content_mismatch,
  upload_incomplete: m.error_upload_incomplete,
  storage_unavailable: m.error_storage_unavailable,
  erp_file_invalid: m.error_erp_file_invalid,
  erp_file_settled: m.error_erp_file_settled,
  reference_record_invalid: m.error_reference_record_invalid,
  reference_record_in_use: m.error_reference_record_in_use,
  parameter_change_invalid: m.error_parameter_change_invalid,
  parameter_change_in_effect: m.error_parameter_change_in_effect,
  code_mappings_invalid: m.error_code_mappings_invalid,
  gate_invalid: m.error_gate_invalid,
  sales_campaign_name_taken: m.error_sales_campaign_name_taken,
  sales_campaign_invalid: m.error_sales_campaign_invalid,
  sales_campaign_archived: m.error_sales_campaign_archived,
  brand_availability_invalid: m.error_brand_availability_invalid,
  appointment_not_bookable: m.error_appointment_not_bookable,
  purchase_order_not_editable: m.error_purchase_order_not_editable,
  purchase_order_invalid: m.error_purchase_order_invalid,
  purchase_order_not_ready_for_approval: m.error_purchase_order_not_ready_for_approval,
  purchase_order_lines_invalid: m.error_purchase_order_lines_invalid,
  purchase_order_not_ready_for_confirmation: m.error_purchase_order_not_ready_for_confirmation,
  po_changes_invalid: m.error_po_changes_invalid,
  purchase_order_not_ready_for_sync: m.error_purchase_order_not_ready_for_sync,
  inbound_not_ready_for_dispatch: m.error_inbound_not_ready_for_dispatch,
  inbound_not_ready_for_release: m.error_inbound_not_ready_for_release,
  inbound_sync_not_retryable: m.error_inbound_sync_not_retryable,
  inbound_erp_invoice_not_ready: m.error_inbound_erp_invoice_not_ready,
  inbound_exception_not_open: m.error_inbound_exception_not_open,
  extra_units_inbound_fixed: m.error_extra_units_inbound_fixed,
  extra_units_not_on_hold: m.error_extra_units_not_on_hold,
  missing_items_not_open: m.error_missing_items_not_open,
  missing_items_invalid: m.error_missing_items_invalid,
  credit_note_not_open: m.error_credit_note_not_open,
  credit_note_invalid: m.error_credit_note_invalid,
  purchase_order_sync_not_retryable: m.error_purchase_order_sync_not_retryable,
  inbound_invalid: m.error_inbound_invalid,
  inbound_not_editable: m.error_inbound_not_editable,
  inbound_not_trackable: m.error_inbound_not_trackable,
  inbound_pickup_not_editable: m.error_inbound_pickup_not_editable,
  inbound_not_ready_for_submission: m.error_inbound_not_ready_for_submission,
  inbound_documents_invalid: m.error_inbound_documents_invalid,
  tolerance_bands_invalid: m.error_tolerance_bands_invalid,
  packing_list_invalid: m.error_packing_list_invalid,
  packing_list_extraction_not_allowed: m.error_packing_list_extraction_not_allowed,
  packing_list_extraction_not_ready: m.error_packing_list_extraction_not_ready,
  demand_run_invalid: m.error_demand_run_invalid,
  user_invalid: m.error_user_invalid,
  user_email_taken: m.error_user_email_taken,
  user_email_locked: m.error_user_email_locked,
  user_roles_invalid: m.error_user_roles_invalid,
  own_access: m.error_own_access,
  user_administered_by_tomorrow: m.error_user_administered_by_tomorrow,
  user_not_administrable: m.error_user_not_administrable,
  not_external_user: m.error_not_external_user,
  invitation_not_pending: m.error_invitation_not_pending,
  invitation_not_sent: m.error_invitation_not_sent,
  mfa_not_enrolled: m.error_mfa_not_enrolled,
  callback_contact_invalid: m.error_callback_contact_invalid,
  mfa_reset_pending: m.error_mfa_reset_pending,
  mfa_reset_not_pending: m.error_mfa_reset_not_pending,
  mfa_reset_unavailable: m.error_mfa_reset_unavailable,
  delegation_not_allowed: m.error_delegation_not_allowed,
  delegated_access_invalid: m.error_delegated_access_invalid,
  access_token_limit_reached: m.error_access_token_limit_reached,
  access_tokens_unavailable: m.error_access_tokens_unavailable,
  access_token_invalid: m.error_access_token_invalid,
  mcp_disabled: m.error_mcp_disabled,
  connection_permissions_invalid: m.error_connection_permissions_invalid,
  prepared_action_not_pending: m.error_prepared_action_not_pending,
  prepared_action_invalid: m.error_prepared_action_invalid,
  oauth_client_exists: m.error_oauth_client_exists,
  role_invalid: m.error_role_invalid,
  role_code_taken: m.error_role_code_taken,
  role_delegation_invalid: m.error_role_delegation_invalid,
  role_permissions_invalid: m.error_role_permissions_invalid,
  role_in_use: m.error_role_in_use,
  organization_invalid: m.error_organization_invalid,
  organization_internal: m.error_organization_internal,
  organization_merge_invalid: m.error_organization_merge_invalid,
  organization_has_open_orders: m.error_organization_has_open_orders,
  organization_brand_scope_of_supplier: m.error_organization_brand_scope_of_supplier,
  address_invalid: m.error_address_invalid,
  contact_invalid: m.error_contact_invalid,
  manufacturer_invalid: m.error_manufacturer_invalid,
  order_confirmation_recipient_invalid: m.error_order_confirmation_recipient_invalid,
  country_area_manager_invalid: m.error_country_area_manager_invalid,
  etaf_invalid: m.error_etaf_invalid,
  etaf_not_ready_for_approval: m.error_etaf_not_ready_for_approval,
  etaf_not_ready_for_submission: m.error_etaf_not_ready_for_submission,
  etaf_link_invalid: m.error_etaf_link_invalid,
  etaf_link_contact_invalid: m.error_etaf_link_contact_invalid,
  etaf_link_not_sent: m.error_etaf_link_not_sent,
  booking_link_invalid: m.error_booking_link_invalid,
  booking_link_contact_invalid: m.error_booking_link_contact_invalid,
  booking_link_not_sent: m.error_booking_link_not_sent,
  campaign_invitation_invalid: m.error_campaign_invitation_invalid,
  organization_not_ready_for_bc: m.error_organization_not_ready_for_bc,
  organization_bc_sync_not_allowed: m.error_organization_bc_sync_not_allowed,
  bc_record_match_invalid: m.error_bc_record_match_invalid,
  hubspot_company_not_confirmable: m.error_hubspot_company_not_confirmable,
  hubspot_reading_not_decidable: m.error_hubspot_reading_not_decidable,
  contact_association_invalid: m.error_contact_association_invalid,
  contact_not_erasable: m.error_contact_not_erasable,
  contact_erasure_confirmation_wrong: m.error_contact_erasure_confirmation_wrong,
  contact_erasure_in_progress: m.error_contact_erasure_in_progress,
  buying_group_invalid: m.error_buying_group_invalid,
  joor_order_not_applicable: m.error_joor_order_not_applicable,
  payment_flag_not_changeable: m.error_payment_flag_not_changeable,
  allocation_run_in_progress: m.error_allocation_run_in_progress,
  allocation_not_configured: m.error_allocation_not_configured,
  sales_order_warehouse_not_assignable: m.error_sales_order_warehouse_not_assignable,
  allocation_not_changeable: m.error_allocation_not_changeable,
  sales_order_not_lockable: m.error_sales_order_not_lockable,
  sales_order_not_ready_for_lock: m.error_sales_order_not_ready_for_lock,
  sales_order_not_ready_for_sync: m.error_sales_order_not_ready_for_sync,
  sales_order_sync_not_retryable: m.error_sales_order_sync_not_retryable,
  sales_order_fulfillment_not_resumable: m.error_sales_order_fulfillment_not_resumable,
  sales_order_uncovered_not_settleable: m.error_sales_order_uncovered_not_settleable,
  order_confirmation_run_in_progress: m.error_order_confirmation_run_in_progress,
  order_confirmations_not_issuable: m.error_order_confirmations_not_issuable,
  order_confirmation_not_decidable: m.error_order_confirmation_not_decidable,
  order_confirmation_already_decided: m.error_order_confirmation_already_decided,
  short_shipment_already_credited: m.error_short_shipment_already_credited,
  credit_control_step_not_recordable: m.error_credit_control_step_not_recordable,
  hilldun_approval_not_recordable: m.error_hilldun_approval_not_recordable,
  allocation_schedules_invalid: m.error_allocation_schedules_invalid,
  allocation_settings_invalid: m.error_allocation_settings_invalid,
  allocation_settings_scheduled: m.error_allocation_settings_scheduled,
  allocation_setting_not_withdrawable: m.error_allocation_setting_not_withdrawable,
  allocation_trial_too_large: m.error_allocation_trial_too_large,
  document_not_retryable: m.error_document_not_retryable,
  document_pdf_not_requestable: m.error_document_pdf_not_requestable,
  document_erp_invoice_not_ready: m.error_document_erp_invoice_not_ready,
  invoice_before_shipment: m.error_invoice_before_shipment,
  sales_order_not_fulfillable: m.error_sales_order_not_fulfillable,
  fulfillment_not_releasable: m.error_fulfillment_not_releasable,
  fulfillment_not_ready_for_release: m.error_fulfillment_not_ready_for_release,
  fulfillment_sync_not_retryable: m.error_fulfillment_sync_not_retryable,
  shipment_tracking_not_allowed: m.error_shipment_tracking_not_allowed,
  shipment_booking_not_allowed: m.error_shipment_booking_not_allowed,
  shipment_booking_not_ready: m.error_shipment_booking_not_ready,
  stock_movement_not_ready: m.error_stock_movement_not_ready,
  vendor_return_credit_not_open: m.error_vendor_return_credit_not_open,
  stock_movement_not_cancellable: m.error_stock_movement_not_cancellable,
  stock_movement_sync_not_retryable: m.error_stock_movement_sync_not_retryable,
  stock_adjustment_not_ready: m.error_stock_adjustment_not_ready,
  stock_adjustment_sync_not_retryable: m.error_stock_adjustment_sync_not_retryable,
  template_invalid: m.error_template_invalid,
  template_read_only: m.error_template_read_only,
  template_default_locale: m.error_template_default_locale,
  recipient_list_invalid: m.error_recipient_list_invalid,
  suppression_invalid: m.error_suppression_invalid,
  preference_invalid: m.error_preference_invalid,
  follow_limit_reached: m.error_follow_limit_reached,
  push_unavailable: m.error_push_unavailable,
  push_subscription_invalid: m.error_push_subscription_invalid,
  push_endpoint_taken: m.error_push_endpoint_taken,
  payload_not_archived: m.error_payload_not_archived,
  claim_invalid: m.error_claim_invalid,
  claim_id_taken: m.error_claim_id_taken,
  claim_ticket_not_retryable: m.error_claim_ticket_not_retryable,
  claim_replacement_invalid: m.error_claim_replacement_invalid,
  claim_credit_not_open: m.error_claim_credit_not_open,
  claim_replacement_not_creatable: m.error_claim_replacement_not_creatable,
  assistant_disabled: m.error_assistant_disabled,
  assistant_cap_reached: m.error_assistant_cap_reached,
  assistant_busy: m.error_assistant_busy,
  assistant_unavailable: m.error_assistant_unavailable,
  assistant_not_allowed: m.error_assistant_not_allowed,
  assistant_not_in_room: m.error_assistant_not_in_room,
};
